/**
 * Patch 37.4.43 · useIsMobile — small viewport query hook shared
 * across surfaces (Library drawer, Content Ops drawer) that need to
 * swap a desktop right-side drawer for a mobile bottom sheet.
 *
 * Uses ``window.matchMedia`` under a Tailwind ``md:`` equivalent
 * (min-width: 768px). SSR-safe — returns ``false`` before the media
 * query resolves so the desktop layout is the default and hydration
 * mismatches never happen on Node-rendered pages.
 */
import { useEffect, useState } from "react";

const MOBILE_BREAKPOINT = "(max-width: 767px)";

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_BREAKPOINT).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(MOBILE_BREAKPOINT);
    const handler = (ev: MediaQueryListEvent) => setIsMobile(ev.matches);
    // Sync in case the SSR default disagrees with the first client
    // resolution (e.g. a phone with orientation-change during boot).
    setIsMobile(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    // Safari < 14
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  return isMobile;
}
